import {
	Box,
	FormControl,
	FormControlLabel,
	FormHelperText,
	InputLabel,
	MenuItem,
	Select,
	Switch,
} from "@mui/material";
import TextField from "@mui/material/TextField";
import { SalutationType, useUpdateContactDeleteMutation } from "@repo/server-api";
import { useTranslation } from "next-i18next";
import React, {
	forwardRef,
	useCallback,
	useEffect,
	useImperativeHandle,
	useMemo,
	useRef,
	useState,
} from "react";
import * as Yup from "yup";

import { setContactValues } from "@repo/shared/common";
import { useNotification } from "@repo/shared/components";

import type { ContactExtended } from "@repo/server-api/types";

type Exposed = {
	validate: () => Promise<Record<string, boolean | object | string>>;
};

export const PersonIdentification = forwardRef(
	(
		props: Readonly<{
			isDialog?: boolean;
			contact: ContactExtended | null;
			setContact?: (value: ContactExtended) => void;
			errors?: Record<string, boolean | object | string>;
			setErrors?: (value: Record<string, boolean | object | string>) => void;
			readOnly?: boolean;
		}>,
		ref: React.Ref<Exposed>,
	) => {
		const { t } = useTranslation();

		const { showNotification } = useNotification();

		const info = useMemo<ContactExtended>(() => {
			return setContactValues(props.contact);
		}, [props.contact]);

		const [firstNameLocal, setFirstNameLocal] = useState(info.firstName ?? "");
		const [lastNameLocal, setLastNameLocal] = useState(info.lastName ?? "");
		const salutation = info.salutation ?? "";
		const title = info.title ?? "";

		const [contactNumberLocal, setContactNumberLocal] = useState(info.contactNumber ?? "");

		const firstFocusedRef = useRef(false);
		const lastFocusedRef = useRef(false);
		const numberFocusedRef = useRef(false);

		const [deleteContact] = useUpdateContactDeleteMutation();

		useEffect(() => {
			if (!firstFocusedRef.current) setFirstNameLocal(info.firstName ?? "");
		}, [info.firstName]);

		useEffect(() => {
			if (!lastFocusedRef.current) setLastNameLocal(info.lastName ?? "");
		}, [info.lastName]);

		const validationSchema = useMemo(
			() =>
				Yup.object().shape({
					firstName: Yup.string().required(t("first-name.required", { ns: "validation" })),
					lastName: Yup.string().required(t("last-name.required", { ns: "validation" })),
					salutation: Yup.string().required(t("salutation.required", { ns: "validation" })),
				}),
			[t],
		);

		const validateFields = useCallback(async () => {
			const localErrors: Record<string, boolean | object | string> = {};
			try {
				await validationSchema.validate(
					{
						firstName: firstNameLocal,
						lastName: lastNameLocal,
						salutation,
					},
					{ abortEarly: false },
				);
				props.setErrors?.({});
				return localErrors;
			} catch (err) {
				if (err instanceof Yup.ValidationError) {
					err.inner.forEach((error) => {
						if (error.path != null) {
							localErrors[error.path] = error.message;
						}
					});
					props.setErrors?.({ ...localErrors });
				}
				return localErrors;
			}
		}, [firstNameLocal, lastNameLocal, salutation, props, validationSchema]);

		useImperativeHandle(ref, () => ({
			validate: validateFields,
		}));

		const setInContact = (patch: Partial<ContactExtended>): void => {
			if (props.contact == null) return;
			props.setContact?.({ ...props.contact, ...patch } as ContactExtended);
		};

		return (
			<div>
				{props.isDialog !== true ? (
					<Box className="fullCenter">
						<FormControlLabel
							control={
								<Switch
									checked={!info.isDeleted}
									onChange={(event) => {
										const v = !event.target.checked;
										void deleteContact({
											variables: { id: info.id, isDeleted: v },
										}).then(() => {
											if (v) showNotification(t("message.contact-inactivated"), "success");
											else showNotification(t("message.contact-restored"), "success");
											setInContact({ isDeleted: v });
										});
									}}
								/>
							}
							label={t("Active")}
						/>
					</Box>
				) : (
					""
				)}
				<Box sx={{ display: "flex", gap: "2%", width: "49%", mt: 2.5 }}>
					<FormControl sx={{ width: "50%" }}>
						<InputLabel className={props.errors?.salutation != null ? "red" : ""} id="salutation">
							{t("salutation")}
						</InputLabel>
						<Select
							inputProps={{ readOnly: props.readOnly ?? false }}
							labelId="salutation"
							id="select-salutation"
							value={salutation}
							error={Boolean(props.errors?.salutation)}
							label={t("salutation")}
							onChange={(event) => {
								setInContact({
									...info,
									salutation: event.target.value as SalutationType,
								});
								props.setErrors?.({ ...props.errors, salutation: "" });
							}}>
							<MenuItem value={SalutationType.Mr}>{t("salutations.mr")}</MenuItem>
							<MenuItem value={SalutationType.Mrs}>{t("salutations.mrs")}</MenuItem>
						</Select>
						{props.errors?.salutation != null ? (
							<FormHelperText className="red">{props.errors.salutation as string}</FormHelperText>
						) : null}
					</FormControl>
					<FormControl sx={{ width: "50%" }}>
						<InputLabel id="title">{t("title")}</InputLabel>
						<Select
							inputProps={{ readOnly: props.readOnly ?? false }}
							labelId="title"
							id="select-title"
							value={title}
							label={t("title")}
							onChange={(event) => {
								setInContact({ ...info, title: event.target.value });
								props.setErrors?.({ ...props.errors, title: "" });
							}}>
							<MenuItem value="">{t("title.no-title")}</MenuItem>
							<MenuItem value="PhD">PhD</MenuItem>
							<MenuItem value="MSc">MSc</MenuItem>
							<MenuItem value="Dr.">Dr.</MenuItem>
							<MenuItem value="Prof.">Prof.</MenuItem>
						</Select>
					</FormControl>
				</Box>
				<div className="clear" />
				<Box sx={{ display: "flex", gap: "2%", mt: 2.5 }}>
					<TextField
						inputProps={{ readOnly: props.readOnly ?? false }}
						onChange={(event) => {
							const v = event.target.value ?? "";
							setFirstNameLocal(v);
							const displayName = `${v} ${lastNameLocal ?? ""}`.trim();
							setInContact({ ...info, firstName: v, displayName });
							props.setErrors?.({ ...props.errors, firstName: "" });
						}}
						onFocus={() => {
							firstFocusedRef.current = true;
						}}
						onBlur={() => {
							firstFocusedRef.current = false;
							setFirstNameLocal(setContactValues(props.contact).firstName ?? "");
						}}
						error={Boolean(props.errors?.firstName)}
						helperText={props.errors?.firstName as string}
						value={firstNameLocal}
						label={t("first-name")}
						variant="outlined"
						sx={{ width: "50%" }}
					/>
					<TextField
						inputProps={{ readOnly: props.readOnly ?? false }}
						onChange={(event) => {
							const v = event.target.value ?? "";
							setLastNameLocal(v);
							const displayName = `${firstNameLocal ?? ""} ${v}`.trim();
							setInContact({ ...info, lastName: v, displayName });
							props.setErrors?.({ ...props.errors, lastName: "" });
						}}
						onFocus={() => {
							lastFocusedRef.current = true;
						}}
						onBlur={() => {
							lastFocusedRef.current = false;
							setLastNameLocal(setContactValues(props.contact).lastName ?? "");
						}}
						error={Boolean(props.errors?.lastName)}
						helperText={props.errors?.lastName as string}
						value={lastNameLocal}
						label={t("last-name")}
						variant="outlined"
						sx={{ width: "50%" }}
					/>
				</Box>
				{props.isDialog === true ? (
					""
				) : (
					<Box className="fullCenter">
						<TextField
							onChange={(event) => {
								const v = event.target.value ?? "";
								setContactNumberLocal(v);
								setInContact({ contactNumber: v });
							}}
							onFocus={() => {
								numberFocusedRef.current = true;
							}}
							onBlur={() => {
								numberFocusedRef.current = false;
								setContactNumberLocal(setContactValues(props.contact).contactNumber ?? "");
							}}
							error={Boolean(props.errors?.contactNumber)}
							helperText={props.errors?.contactNumber as string}
							value={contactNumberLocal}
							label={t("contact-number")}
							variant="outlined"
							sx={{ float: "left", width: "100%", marginTop: "10px" }}
						/>
					</Box>
				)}
			</div>
		);
	},
);

PersonIdentification.displayName = "PersonIdentification";
